import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import Footer from "./components/Footer";
import LoginPage from "./components/LoginPage";
import AdminLogin from "./components/AdminLogin";
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import Dashboard from "./components/Dashboard";
import FlipbookReader from "./components/FlipbookReader";
import AdminDashboard from "./components/AdminDashboard";
import UpgradePage from "./components/UpgradePage";
import ReadathonPage from "./components/ReadathonPage";
import ReadathonDetail from "./components/ReadathonDetail";
import { PaymentSuccessPage, PaymentCancelPage } from "./components/PaymentPages";
// New separate pages
import AboutPage from "./components/pages/AboutPage";
import ResourcesPage from "./components/pages/ResourcesPage";
import FaithPage from "./components/pages/FaithPage";
import TeamPage from "./components/pages/TeamPage";
import ContactPage from "./components/pages/ContactPage";
import UnsubscribePage from "./components/pages/UnsubscribePage";
import EventsPage from "./components/pages/EventsPage";
import DonatePage from "./components/pages/DonatePage";
import JoinTeamPage from "./components/pages/JoinTeamPage";
import ShopPage from "./components/pages/ShopPage";
import PolicyPage from "./components/pages/PolicyPage";
import MyDataPage from "./components/pages/MyDataPage";
import BibleQuizPage from "./components/pages/BibleQuizPage";
import InvoicePage from "./components/pages/InvoicePage";

import { CurrencyProvider } from "./context/CurrencyContext";
import { BasketProvider } from "./context/BasketContext";
import CookieBanner from "./components/CookieBanner";
import InstallPrompt from "./components/InstallPrompt";
import SupportBanner from "./components/SupportBanner";

// Landing Page Component - Now just hero section
const LandingPage = () => {
  const [testimonials, setTestimonials] = React.useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = React.useState(false);
  const [testimonialText, setTestimonialText] = React.useState('');
  const [testimonialRating, setTestimonialRating] = React.useState(5);
  const [testimonialRole, setTestimonialRole] = React.useState('');
  const [submittingTestimonial, setSubmittingTestimonial] = React.useState(false);
  const auth = useAuth() || {};
  const isAuthenticated = auth.isAuthenticated || false;
  const navigate = useNavigate();

  React.useEffect(() => {
    // Fetch testimonials from API
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setTestimonials(data))
      .catch(() => setTestimonials([]));
  }, []);

  return (
    <>
      <Header />
      <main>
        <HeroSection />
        
        {/* Testimonials Section */}
        {testimonials.length > 0 && (
          <section className="py-16 bg-gradient-to-b from-amber-50 to-white">
            <div className="max-w-6xl mx-auto px-4">
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 bg-coral-50 text-coral-600 px-4 py-2 rounded-full mb-4">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  <span className="text-sm font-medium">Testimonials</span>
                </span>
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-800 mb-4">
                  What People Say
                </h2>
                <p className="text-slate-600 max-w-2xl mx-auto">
                  Hear from families and educators who use Creassion Resources
                </p>
              </div>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {testimonials.slice(0, 6).map((testimonial, index) => (
                  <div key={testimonial.testimonial_id || index} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-1 text-amber-400 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                        </svg>
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">"{testimonial.content || testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold">
                        {(testimonial.author_name || testimonial.author)?.charAt(0) || 'A'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{testimonial.author_name || testimonial.author}</p>
                        <p className="text-sm text-slate-500">{testimonial.author_role || testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Leave a Testimonial CTA */}
              <div className="text-center mt-8">
                <button
                  onClick={() => {
                    if (!isAuthenticated) {
                      navigate('/login');
                      return;
                    }
                    setShowTestimonialForm(true);
                  }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  Leave a Testimonial
                </button>
              </div>
            </div>
          </section>
        )}
        {/* Testimonial Submission Modal */}
        {showTestimonialForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Leave a Testimonial</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Role (optional)</label>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm" value={testimonialRole} onChange={(e) => setTestimonialRole(e.target.value)} placeholder="e.g., Parent, Teacher, Sunday School Leader" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
                  <div className="flex gap-1">{[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => setTestimonialRating(n)} className="p-1">
                      <svg className={`w-6 h-6 ${n <= testimonialRating ? 'text-amber-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                    </button>
                  ))}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Your Testimonial *</label>
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm min-h-[120px]" value={testimonialText} onChange={(e) => setTestimonialText(e.target.value)} placeholder="Share your experience with Creassion Resources..." />
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <button onClick={() => setShowTestimonialForm(false)} className="flex-1 px-4 py-2 border rounded-lg text-slate-600 hover:bg-slate-50">Cancel</button>
                <button
                  onClick={async () => {
                    if (!testimonialText || testimonialText.length < 10) { alert('Please write at least 10 characters'); return; }
                    setSubmittingTestimonial(true);
                    try {
                      const token = localStorage.getItem('session_token');
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/testimonials/submit`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
                        credentials: 'include',
                        body: JSON.stringify({ content: testimonialText, rating: testimonialRating, author_role: testimonialRole })
                      });
                      if (r.ok) {
                        setShowTestimonialForm(false);
                        setTestimonialText('');
                        setTestimonialRole('');
                        setTestimonialRating(5);
                        alert('Thank you! Your testimonial has been submitted for review.');
                      } else {
                        const err = await r.json().catch(() => ({}));
                        alert(err.detail || 'Failed to submit. Please try again.');
                      }
                    } catch (e) { alert('Failed to submit. Please try again.'); }
                    setSubmittingTestimonial(false);
                  }}
                  disabled={submittingTestimonial}
                  className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {submittingTestimonial ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// App Router with session detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check for session_id SYNCHRONOUSLY during render
  // This prevents race conditions by processing auth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/faith" element={<FaithPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/readathon" element={<ReadathonPage />} />
      <Route path="/readathon/:readathonId" element={<ReadathonDetail />} />
      <Route path="/upgrade" element={<UpgradePage />} />
      <Route path="/donate" element={<DonatePage />} />
      <Route path="/support" element={<DonatePage />} />
      <Route path="/join-team" element={<JoinTeamPage />} />
      <Route path="/volunteer" element={<JoinTeamPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/order-success" element={<PaymentSuccessPage />} />
      <Route path="/policy/:slug" element={<PolicyPage />} />
      <Route path="/my-data" element={<MyDataPage />} />
      <Route path="/quiz" element={<BibleQuizPage />} />
      <Route path="/invoice/:invoiceId" element={<InvoicePage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/read/:bookId"
        element={
          <ProtectedRoute>
            <FlipbookReader />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRole="community_manager">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
};

function App() {
  return (
    <div className="App antialiased">
      <Toaster 
        position="bottom-center" 
        toastOptions={{
          style: {
            background: '#fff',
            border: '1px solid #10B98120',
          },
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <BasketProvider>
              <AppRouter />
              <CookieBanner />
              <InstallPrompt />
              <SupportBanner />
            </BasketProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
